// src/App.js
import React, { useEffect, useRef } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { theme } from './theme/theme';
import { GameProvider, useGameContext } from './context/GameContext';
import { LoginScreen } from './components/LoginScreen';
import { HomeScreen } from './components/HomeScreen';
import { Lobby } from './components/Lobby';
import { DrawingGame } from './games/drawing/DrawingGame';
import { LudoGame } from './games/ludo/LudoGame';
import { SnakeLadderGame } from './games/snakeladder/SnakeLadderGame';
import { UnoGame } from './games/uno/UnoGame';
import { MiniGolfGame } from './games/minigolf/MiniGolfGame';
import { QuizGame } from './games/quiz/QuizGame';
import { BoggleGame } from './games/boggle/BoggleGame';
import { GameVoiceChat } from './components/GameVoiceChat';
import { GameAudioControls } from './components/GameAudioControls';
import { playGameSound } from './audio/gameAudio';
import { Box, CircularProgress, Typography } from '@mui/material';

const GAME_COMPONENTS = {
  drawing: DrawingGame,
  ludo: LudoGame,
  snakeladder: SnakeLadderGame,
  uno: UnoGame,
  minigolf: MiniGolfGame,
  quiz: QuizGame,
  boggle: BoggleGame,
};


function AppContent() {
  const { state } = useGameContext();
  const { isAuthReady, isLoggedIn, roomId, room } = state;
  const previousAudioState = useRef(null);

  useEffect(() => {
    if (!roomId || !room) {
      previousAudioState.current = null;
      return;
    }

    const gameStateKey = {
      ludo: 'ludoState',
      snakeladder: 'slState',
      uno: 'unoState',
      minigolf: 'miniGolfState',
      quiz: 'quizState',
      boggle: 'boggleState',
    }[room.gameType];
    const gameState = gameStateKey ? room[gameStateKey] || {} : {};
    const currentAudioState = {
      roomId,
      gameType: room.gameType,
      status: room.status,
      round: room.currentRound ?? gameState.currentHoleIdx ?? gameState.currentQuestionIndex,
      phase: gameState.phase,
      turn: room.currentDrawer ?? gameState.currentTurn ?? gameState.currentTurnIndex ??
        gameState.currentIndex ?? gameState.currentPlayer ?? gameState.currentPlayerId,
      dice: gameState.diceValue ?? gameState.lastRoll,
      winner: gameState.winner,
      lastAction: JSON.stringify(gameState.lastAction || null),
      activity: room.gameType === 'boggle' ? null : JSON.stringify(
        gameState.positions || gameState.pieces || gameState.balls ||
        gameState.submissions || gameState.answers ||
        gameState.discardPile?.length || gameState.turnCount || null
      ),
      scores: JSON.stringify(gameState.scores || Object.values(room.players || {}).map(player => player.score || 0)),
    };

    const previous = previousAudioState.current;
    previousAudioState.current = currentAudioState;
    if (!previous || previous.roomId !== roomId || previous.gameType !== room.gameType) return;
    if (previous.status !== currentAudioState.status) {
      if (currentAudioState.status === 'finished' || currentAudioState.winner) playGameSound('win');
      else if (currentAudioState.status === 'playing') playGameSound('start');
      else if (currentAudioState.status === 'roundEnd') playGameSound('move');
      return;
    }
    if (previous.round !== currentAudioState.round) {
      playGameSound('start');
    } else if (previous.winner !== currentAudioState.winner && currentAudioState.winner) {
      playGameSound('win');
    } else if (previous.dice !== currentAudioState.dice && currentAudioState.dice != null) {
      playGameSound('roll');
    } else if (previous.lastAction !== currentAudioState.lastAction && currentAudioState.lastAction) {
      playGameSound('card');
    } else if (previous.scores !== currentAudioState.scores) {
      playGameSound('correct');
    } else if (previous.phase !== currentAudioState.phase && currentAudioState.phase) {
      playGameSound('start');
    } else if (previous.activity !== currentAudioState.activity) {
      playGameSound(room.gameType === 'uno' ? 'card' : 'move');
    } else if (previous.turn !== currentAudioState.turn && currentAudioState.turn != null) {
      playGameSound('move');
    }
  }, [roomId, room]);

  if (!isAuthReady) {
    return (
      <Box sx={{ height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#080c12' }}>
        <CircularProgress sx={{ color: '#4CC9F0' }} />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return (
      <motion.div key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ height: '100dvh' }}>
        <LoginScreen />
      </motion.div>
    );
  }

  const screen = !roomId ? 'home'
    : !room ? 'home'
    : room.status === 'waiting' ? 'lobby'
    : 'game';

  const GameComp = GAME_COMPONENTS[room?.gameType] || DrawingGame;

  const isGameStateReady = () => {
    if (!room) return false;
    switch (room.gameType) {
      case 'drawing': return !!room.currentDrawer;
      case 'ludo': return !!room.ludoState;
      case 'snakeladder': return !!room.slState;
      case 'uno': return !!room.unoState;
      case 'minigolf': return !!room.miniGolfState;
      case 'quiz': return !!room.quizState;
      case 'boggle': return !!room.boggleState;
      default: return true;
    }
  };

  return (
    <>
      {isLoggedIn && (
        <Box
          component="nav"
          aria-label="Voice and game audio controls"
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.35,
            boxSizing: 'border-box',
            height: 'max(72px, calc(env(safe-area-inset-bottom) + 64px))',
            pb: 'env(safe-area-inset-bottom)',
            bgcolor: 'rgba(8,12,18,0.98)',
            borderTop: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 -8px 28px rgba(0,0,0,0.36)',
            backdropFilter: 'blur(14px)',
          }}
        >
          <GameAudioControls docked />
          {roomId && room && <GameVoiceChat key={roomId} roomId={roomId} docked />}
        </Box>
      )}
      <AnimatePresence mode="wait">
        {screen === 'home' && (
          <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <HomeScreen />
          </motion.div>
        )}
        {screen === 'lobby' && (
          <motion.div key="lobby" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <Lobby />
          </motion.div>
        )}
        {screen === 'game' && (
          <motion.div key="game" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} style={{ height: '100dvh' }}>
            {isGameStateReady() ? (
              <GameComp />
            ) : (
              <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#080c12', color: 'white' }}>
                <CircularProgress sx={{ color: '#4CC9F0', mb: 2 }} />
                <Typography sx={{ fontWeight: 700, color: '#8b949e' }}>Loading game state...</Typography>
              </Box>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Global escape hatch — floats over any game screen */}
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GameProvider>
        <AppContent />
      </GameProvider>
    </ThemeProvider>
  );
}